import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { useCallback, useEffect, useRef, useState } from "react";
import { setColumnWidth } from "../../../helpers/cookieUtils";
import ListingHeader from "../listingHeader/ListingHeader";
import {
    Avatar,
    Popover,
    PopoverTrigger,
    PopoverSurface,
    TableCellLayout,
    makeStyles,
    Tooltip
} from "@fluentui/react-components";
import HoverCard from "../../hoverCard/HoverCard";
import {
    getEditPermission,
    getDeletePermission,
    formatMonthYear,
    getReshuffleStatusLabel,
    getReshuffleStatusColorClass,
    ReshuffleRequestStatus,
    compareArrays,
    sortColumnsByOrderlist,
    formatPeriodMonthYear
} from "../../../helpers/utils";
import Spin from "../spinner/Spin";
import { AttachementIcon, DeleteIcon, EditIcon, FlagIcon, MeatBallsMenuIcon } from "../../../modules/default-svg";
import { createSkeletonRows, useTableContextChange } from "./utils/tableUtils";
import { generalActions } from "../../../store/actions";
import userService from "../../../store/services/user.service";
import { displayDecimalFormatted, utcToLocalDateTimeString } from "../../../utils/systemSettings";
import { updateReshuffleRequestFlag } from "../../../store/actions/reshuffleRequests.actions";
import { ArchiveRestore, Check, CircleSlash, Eye, Pencil, Play, Presentation, X } from "lucide-react";

const useStyles = makeStyles({
    tooltip: {
        backgroundColor: "#342c82",
        color: "#FFFFFF",
    },
});

const ReshuffleRequestTable = ({
    columnsList,
    rowData,
    setIsCreate,
    searchHandler,
    tableName,
    handleColumnOrdering,
    dispatch,
    setPageNum,
    perPageItems,
    pagination,
    handleEdit,
    handleDelete,
    parentType,
    projectId,
    loading,
    isHeader,
    triggerSearch,
    handleAction,
    setRowData,
}) => {

    const styles = useStyles();
    const [cols, setCols] = useState([]);
    const [rows, setRows] = useState([]);
    const [colsList, setColumnsList] = useState([]);
    const [popoverStates, setPopoverStates] = useState({});
    const [editingHeader, setEditingHeader] = useState(null);
    const [headerValue, setHeaderValue] = useState("");
    const [columnSizing, setColumnSizing] = useState({});
    const [columnOrder, setColumnOrder] = useState([]);
    const [sorting, setSorting] = useState([]);
    const [isFetchingMore, setIsFetchingMore] = useState(false);

    const setting = JSON.parse(localStorage.getItem("settings"));
    const tableContainerRef = useRef(null);
    const lastScrollTop = useRef(0);
    const selectRefs = useRef({});
    const canEdit = getEditPermission("ReshuffleRequest");
    const canDelete = getDeletePermission("ReshuffleRequest");


    const { hasContextChanged } = useTableContextChange({
        contextValues: {
            projectId,
            parentType
        },
        setIsFetchingMore,
        setRows,
        setRowData,
    });
    const handlePopoverChange = (id, open) => {
        setPopoverStates((prev) => ({ ...prev, [id]: open }));
    };

    const closePopover = (e, id) => {
        e.stopPropagation();
        e.preventDefault();
        handlePopoverChange(
            id,
            !popoverStates[id]
        );
    }


    useEffect(() => {
        if (columnsList.length) {
            setColumnsList(columnsList);
        }
    }, [columnsList]);

    const handleHeaderDoubleClick = (columnId, currentHeader) => {
        setEditingHeader(columnId);
        setHeaderValue(currentHeader);
    };

    const handleHeaderSave = (columnId) => {
        if (!headerValue.trim()) {
            dispatch(
                generalActions.showToast({
                    title: "Header name cannot be empty",
                    type: "error",
                })
            );
            return;
        }

        const updatedColumnsDB = cols.map((col) => ({
            name: col.Label,
            customName: col.accessorKey === columnId ? headerValue : col.CustomName,
            order: col.Order,
            isVisible: col.Visibility,
        }));

        const payload = {
            Name: tableName,
            Columns: updatedColumnsDB,
        };

        userService
            .putUserLayoutRequest(payload)
            .then(() => { })
            .catch(() => {
                dispatch(
                    generalActions.showToast({
                        title: "Error updating column header",
                        type: "error",
                    })
                );
            })
            .finally(() => {
                setColumnsList((prevCols) => {
                    const newCols = prevCols.map((col) =>
                        col.accessorKey === columnId
                            ? { ...col, header: headerValue, customName: headerValue }
                            : col
                    );
                    return newCols;
                });
            });

        setEditingHeader(null);
        setHeaderValue("");
    };

    const handleFlag = (id, status) => {
        const newData = [...rowData];
        const index = newData.findIndex((item) => item.id === id);
        if (index !== -1) {
            newData[index].isFlagged = status;
        }

        setRowData(
            newData.sort((a, b) => {
                return b.isFlagged - a.isFlagged;
            })
        );
        dispatch(
            updateReshuffleRequestFlag({
                id: id,
                projectId: projectId,
                payload: [
                    {
                        op: "replace",
                        path: "/IsFlagged",
                        value: status,
                    },
                ],
            })
        );
    };

    // ── Infinite scroll ────────────────────────────────────────────────────────
    const fetchMoreData = useCallback(() => {
        if (!pagination?.HasNext || loading || isFetchingMore || hasContextChanged) return;
        setIsFetchingMore(true);
        setPageNum(pagination.CurrentPage + 1);
    }, [pagination, loading, isFetchingMore, setPageNum, hasContextChanged]);

    useEffect(() => {
        if (!loading && isFetchingMore) {
            setIsFetchingMore(false);
        }
    }, [loading, rowData.length]);

    const fetchMoreOnBottomReached = useCallback(
        (containerRefElement) => {
            if (!containerRefElement || loading || !pagination?.HasNext) return;

            const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
            const scrolledToBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 50;
            const isScrollingDown = scrollTop > lastScrollTop.current;

            lastScrollTop.current = scrollTop;

            if (isScrollingDown && scrolledToBottom) {
                fetchMoreData();
            }
        },
        [fetchMoreData, loading, pagination]
    );

    const throttledFetchMore = useCallback(
        (() => {
            let timeoutId;
            return (containerRefElement) => {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => {
                    fetchMoreOnBottomReached(containerRefElement);
                }, 100);
            };
        })(),
        [fetchMoreOnBottomReached]
    );

    useEffect(() => {
        const container = tableContainerRef.current;
        if (container) {
            const handleScroll = () => throttledFetchMore(container);
            container.addEventListener("scroll", handleScroll, { passive: true });
            return () => {
                container.removeEventListener("scroll", handleScroll);
            };
        }
    }, [throttledFetchMore]);

    // Reset scroll position when search is triggered
    useEffect(() => {
        if (tableContainerRef.current && triggerSearch !== undefined) {
            tableContainerRef.current.scrollTop = 0;
            lastScrollTop.current = 0;
        }
    }, [triggerSearch]);

    // ── Sorting setup ───────────────────────────────────────────────────────────
    const getSortingFunction = (col) => {
        switch (col.label) {
            case "Resource":
                return (rowA, rowB, columnId) => {
                    const a = (rowA.original.targetResource?.fullName || "").toLowerCase();
                    const b = (rowB.original.targetResource?.fullName || "").toLowerCase();
                    return a.localeCompare(b);
                };
            case "Requested Hours":
                return (rowA, rowB, columnId) => {
                    const a = parseFloat(rowA.original.requestedHours) || 0;
                    const b = parseFloat(rowB.original.requestedHours) || 0;
                    return a - b;
                };
            case "Period":
                return (rowA, rowB) => {
                    const a = new Date(
                        rowA.original.year,
                        (rowA.original.month) - 1,
                        1
                    );

                    const b = new Date(
                        rowB.original.year,
                        (rowB.original.month) - 1,
                        1
                    );

                    return a.getTime() - b.getTime();
                };
            case "Status":
                return (rowA, rowB, columnId) => {
                    const a = rowA.original.status || 0;
                    const b = rowB.original.status || 0;
                    return a - b;
                };
            case "Creator":
                return (rowA, rowB, columnId) => {
                    const a = (rowA.original.creator?.fullName || "").toLowerCase();
                    const b = (rowB.original.creator?.fullName || "").toLowerCase();
                    return a.localeCompare(b);
                };
            case "Reviewer":
                return (rowA, rowB, columnId) => {
                    const a = (rowA.original.reviewer?.fullName || "").toLowerCase();
                    const b = (rowB.original.reviewer?.fullName || "").toLowerCase();
                    return a.localeCompare(b);
                };
            case "Reviewed At":
                return (rowA, rowB, columnId) => {
                    const a = new Date(rowA.original.reviewedAt || 0);
                    const b = new Date(rowB.original.reviewedAt || 0);
                    return a.getTime() - b.getTime();
                };
            case "Executed At":
                return (rowA, rowB, columnId) => {
                    const a = new Date(rowA.original.executedAt || 0);
                    const b = new Date(rowB.original.executedAt || 0);
                    return a.getTime() - b.getTime();
                };
            case "Change Request":
                return (rowA, rowB, columnId) => {
                    const a = (rowA.original.changeRequest?.name || "").toLowerCase();
                    const b = (rowB.original.changeRequest?.name || "").toLowerCase();
                    return a.localeCompare(b);
                };


            default:
                return "alphanumeric";
        }
    };

    const getSortDescFirst = (col) => {
        const descFirstColumns = [
            "Resource",
            "Requested Hours",
            "Period",
            "Status",
            "Creator",
            "Reviewer",
            "Reviewed At",
            "Executed At",
            "Change Request",
        ];
        return descFirstColumns.includes(col.label);
    };

    // ── Column setup ───────────────────────────────────────────────────────────
    useEffect(() => {
        if (!colsList?.length && !rowData.length) return;

        const baseRows = [...rowData];

        if (isFetchingMore && pagination?.HasNext) {
            const skeletonRows = createSkeletonRows();
            setRows([...baseRows, ...skeletonRows]);
        } else {
            setRows(baseRows);
        }
    }, [rowData, isFetchingMore, pagination?.HasNext, colsList]);


    const renderUserCell = (user) => {
        if (!user) return "";
        return (
            <HoverCard
                HoverCardTrigger={
                    <div className="d-flex align-items-center gap-2" style={{ cursor: "pointer" }}>
                        <Avatar
                            name={user.name}
                            image={{ src: user.image }}
                            color="colorful"
                            size={24}
                        />
                        <span className="text-sm">{user.name}</span>
                    </div>
                }
                AvatarId={user.id}
                AvatarName={user.name}
                AvatarImage={user.image}
                AvatarDesignation={user.title}
                AvatarEmail={user.email}
                AvatarPhone={user.phoneNumber}
                AvatarIsExternal={user.isExternal}
            />
        );
    };

    const renderPrimaryCell = (item) => {
        const status = item.status;
        const isDraft = status === ReshuffleRequestStatus.Draft;
        const isPendingApproval = status === ReshuffleRequestStatus.PendingApproval;
        const isApproved = status === ReshuffleRequestStatus.Approved;
        const isRejected = status === ReshuffleRequestStatus.Rejected;
        const isExecuted = status === ReshuffleRequestStatus.Executed;
        const isCancelled = status === ReshuffleRequestStatus.Cancelled;

        const showView = true;
        const showSummary = true;
        const showEdit = isDraft && canEdit;
        const showSubmit = isDraft && canEdit;
        const showApprove = isPendingApproval && canEdit;
        const showReject = isPendingApproval && canEdit;
        const showCancel = isPendingApproval && canEdit;
        const showExecute = isApproved && canEdit;
        const showDelete = (isDraft || isCancelled) && canDelete;

        if (!showView && !showEdit && !showSubmit && !showApprove && !showReject && !showCancel && !showExecute && !showDelete && !showSummary) {
            return null;
        }

        return (
            <div className="d-flex justify-content-between align-items-center inputWidth">
                <div className="d-flex align-items-center inputWidth">
                    <Tooltip
                        positioning="before"
                        content={{
                            children: item.isFlagged ? "Unflag" : "Flag",
                            className: styles.tooltip,
                        }}
                        relationship="label"
                        withArrow
                    >
                        <span
                            className={`flag ${item.isFlagged ? "flagActive" : ""
                                }`}
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handleFlag(item.id, !item.isFlagged);
                            }}
                        >
                            <FlagIcon />
                        </span>
                    </Tooltip>
                    <span className="d-flex w-full text-truncate cursor-pointer"
                        onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            if (canEdit && showEdit) {
                                handleEdit(item);
                            }
                            else if (showSummary) {
                                handleAction && handleAction("summary", item);
                            }
                            handlePopoverChange(
                                item.id,
                                !popoverStates[item.id]
                            );
                        }}
                    >
                        <div className="flex align-items-center">
                            {item.attachementsCount > 0 && (
                                <div className="mr-1">
                                    <AttachementIcon count={item.attachementsCount} />
                                </div>
                            )}
                            {renderUserCell(item.targetResource)}
                        </div>

                    </span>
                    <div className="meatballs-trigger">
                        <Popover
                            open={popoverStates[item.id]}
                            onOpenChange={(e, data) => handlePopoverChange(item.id, data.open || false)}
                            positioning="below-start"
                        >
                            <PopoverTrigger disableButtonEnhancement>
                                <TableCellLayout
                                    media={<MeatBallsMenuIcon />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handlePopoverChange(item.id, !popoverStates[item.id]);
                                    }}
                                />
                            </PopoverTrigger>
                            <PopoverSurface tabIndex={-1} className="listingPopoverWrapper">
                                <div className="listingPopoverContent">
                                    {showView && (
                                        <div className="popItem" onClick={(e) => { closePopover(e, item.id); handleAction && handleAction("view", item); }}>
                                            <Eye size={16} strokeWidth={1.5} color="#342c82" />
                                            <p className="text-sm-400 ms-12">Quick View</p>
                                        </div>
                                    )}
                                    {showSummary && (
                                        <div className="popItem" onClick={(e) => { closePopover(e, item.id); handleAction && handleAction("summary", item); }}>
                                            <Presentation size={16} strokeWidth={1.5} color="#342c82" />
                                            <p className="text-sm-400 ms-12">Summary</p>
                                        </div>
                                    )}
                                    {showEdit && (
                                        <div className="popItem" onClick={(e) => { closePopover(e, item.id); handleEdit && handleEdit(item); }}>
                                            <Pencil size={16} strokeWidth={1.5} color="#342c82" />
                                            <p className="text-sm-400 ms-12">Edit</p>
                                        </div>
                                    )}
                                    {showSubmit && (
                                        <div className="popItem" onClick={(e) => { closePopover(e, item.id); handleAction && handleAction("submit", item); }}>
                                            <ArchiveRestore size={16} strokeWidth={1.5} color="#342c82" />
                                            <p className="text-sm-400 ms-12">Submit</p>
                                        </div>
                                    )}
                                    {showApprove && (
                                        <div className="popItem" onClick={(e) => { closePopover(e, item.id); handleAction && handleAction("approve", item); }}>
                                            <Check size={16} strokeWidth={1.5} color="#0b6a0b" />
                                            <p className="text-sm-400 ms-12">Approve</p>
                                        </div>
                                    )}
                                    {showReject && (
                                        <div className="popItem" onClick={(e) => { closePopover(e, item.id); handleAction && handleAction("reject", item); }}>
                                            <X size={16} strokeWidth={1.5} color="#c50f1f" />
                                            <p className="text-sm-400 ms-12">Reject</p>
                                        </div>
                                    )}
                                    {showExecute && (
                                        <div className="popItem" onClick={(e) => { closePopover(e, item.id); handleAction && handleAction("execute", item); }}>
                                            <Play size={16} strokeWidth={1.5} color="#0b6a0b" />
                                            <p className="text-sm-400 ms-12">Execute</p>
                                        </div>
                                    )}
                                    {showCancel && (
                                        <div className="popItem" onClick={(e) => { closePopover(e, item.id); handleAction && handleAction("cancel", item); }}>
                                            <CircleSlash size={16} strokeWidth={2} color="#aaaaaa" />
                                            <p className="text-sm-400 ms-12">Cancel</p>
                                        </div>
                                    )}
                                    {showDelete && (
                                        <div className="popItem" onClick={(e) => { closePopover(e, item.id); handleDelete && handleDelete(item); }}>
                                            <DeleteIcon color="#c50f1f" />
                                            <p className="text-sm-400 ms-12">Delete</p>
                                        </div>
                                    )}
                                </div>
                            </PopoverSurface>
                        </Popover>
                    </div>
                </div>
            </div>
        );
    };

    const renderCell = (label, item) => {
        switch (label) {
            case "Resource":
                return renderPrimaryCell(item);

            case "Requested Hours":
                return `${displayDecimalFormatted(setting, item.requestedHours)}`;

            case "Period":
                return `${formatMonthYear(item.year, item.month)}`;

            case "Status":
                return (
                    <div className="flex w-100 justify-center items-center">
                        <span className={`pill w-100 ${getReshuffleStatusColorClass(item.status)}`}>
                            {getReshuffleStatusLabel(item.status)}
                        </span>
                    </div>
                );

            case "Creator":
                return renderUserCell(item.creator);

            case "Reviewer":
                return renderUserCell(item.reviewer);

            case "Reviewed At":
                return utcToLocalDateTimeString(setting, item.reviewedAt);

            case "Executed At":
                return utcToLocalDateTimeString(setting, item.executedAt);
            case "Change Request":
                return item.changeRequest
                    ? item.changeRequest.name
                    : "";

            default:
                return "";
        }
    };

    useEffect(() => {
        if (!colsList?.length && !rowData.length) return;
        const columnsOrder = colsList
            .filter((col) => col.isVisible !== false)
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map((item) => item.accessorKey);
        setColumnOrder(columnsOrder);
        const columns = colsList
            .filter((col) => col.isVisible !== false)
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map((col, index) => {
                return {
                    Label: col.label,
                    CustomName: col.customName,
                    Order: col.order,
                    Visibility: col.isVisible,
                    accessorKey: col.accessorKey,
                    header: col.header,
                    enableHiding: true,
                    enableColumnActions: false,
                    grow: true,
                    enableResizing: true,
                    size: col.size,
                    editVariant: col.columnType,
                    enableGlobalFilter: true,
                    enableSorting: editingHeader === col.accessorKey ? false : true,
                    sortingFn: getSortingFunction(col),
                    sortDescFirst: getSortDescFirst(col),
                    Cell: ({ cell, row }) => {
                        const item = row.original;
                        // Render skeleton for skeleton rows
                        if (item.isSkeleton) {
                            return (
                                <div
                                    style={{
                                        height: '20px',
                                        background: 'linear-gradient(90deg, #e0e0e0 25%, #ECE7FD 50%, #e0e0e0 75%)',
                                        backgroundSize: '200% 100%',
                                        animation: 'shimmer 1.5s infinite',
                                        borderRadius: '4px',
                                        width: '100%'
                                    }}
                                />
                            );
                        }
                        return renderCell(col.label, item);
                    },
                    Edit: ({ cell, row, table }) => { },
                    Header: ({ column }) => {
                        return editingHeader === column.id ? (
                            <input
                                value={headerValue}
                                onChange={(e) => setHeaderValue(e.target.value)}
                                onBlur={() => handleHeaderSave(column.id)}
                                onKeyPress={(e) => {
                                    if (e.key === "Enter") {
                                        handleHeaderSave(column.id);
                                    }
                                }}
                                autoFocus
                                className="header-edit-input"
                                style={{
                                    minWidth: column.getSize() - 60,
                                }}
                            />
                        ) : (
                            <span>{column.columnDef.header}</span>
                        );
                    },
                };
            });
        setCols((prevCols) => {
            if (JSON.stringify(prevCols) !== JSON.stringify(columns)) {
                return columns;
            }
            return prevCols;
        });

        const uniqueRows =
            rowData?.filter(
                (row, index, self) => index === self.findIndex((r) => r.id === row.id)
            ) || [];
        setRows((prevRows) => {
            if (JSON.stringify(prevRows) !== JSON.stringify(uniqueRows)) {
                return uniqueRows;
            }
            return prevRows;
        });
    }, [colsList, rowData, editingHeader, headerValue]);

    // ── Table instance ─────────────────────────────────────────────────────────
    const table = useMaterialReactTable({
        columns: cols,
        data: rows,
        enableHiding: true,
        enableSorting: true,
        manualSorting: false,
        projectId: projectId,
        parentType: parentType,
        visibleInShowHideMenu: false,
        enablePagination: false,
        enableTopToolbar: true,
        enableGlobalFilter: true,
        enableStickyHeader: true,
        enableColumnFilterModes: true,
        enableColumnDragging: true,
        enableColumnOrdering: true,
        enableColumnResizing: true,
        columnResizeMode: "onChange",
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        editDisplayMode: "cell",
        globalFilterFn: "contains",
        enableEditing: false,
        enableFilters: false,
        enableCreatingRow: true,
        createDisplayMode: "row",
        positionCreatingRow: "bottom",
        initialState: {
            density: "compact",
            showGlobalFilter: false,
            showColumnFilters: false,
            enableHiding: false,
            columnOrder: cols.map((col) => col.order),
        },
        state: {
            sorting,
            isLoading: rows.length === 0 && loading,
            columnSizing,
            columnOrder,
            showLoadingOverlay: false,
            showProgressBars: false,
        },
        onSortingChange: setSorting,
        positionGlobalFilter: "left",
        onColumnSizingChange: (updater) => {
            setColumnSizing((prev) => {
                const newSizing =
                    typeof updater === "function" ? updater(prev) : updater;
                Object.entries(newSizing).forEach(([columnId, width]) => {
                    setColumnWidth(tableName, columnId, width);
                });
                return newSizing;
            });
        },
        onColumnOrderChange: (updater) => {
            const newOrder = typeof updater === "function" ? updater() : updater;
            const neworderList = newOrder
                .filter((item) => item !== "mrt-row-spacer")
                .filter(Boolean);
            const areEqual = compareArrays(neworderList, columnOrder);
            if (areEqual) {
                return newOrder;
            }

            const sortedColumns = sortColumnsByOrderlist(colsList, neworderList);
            handleColumnOrdering(sortedColumns);
            setColumnOrder(neworderList);
            return neworderList;
        },
        muiTableHeadCellProps: ({ column }) => ({
            sx: {
                fontFamily: "Figtree-Medium",
                fontSize: "14px",
                padding: "1rem 0.5rem 1rem 1rem",
                backgroundColor: "#f5f5f5",
                height: "40px",
                fontWeight: 600,
                "&:hover": {
                    backgroundColor: "#ece8fd",
                },
                position: "relative",
                "& .Mui-TableHeadCell-Content": {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    height: "100%",
                },
                "& .MuiTableSortLabel-root": {
                    marginLeft: "8px",
                    paddingTop: "3px",
                },
            },
            onDoubleClick: (e) => {
                e.stopPropagation();
                e.preventDefault();
                handleHeaderDoubleClick(column.id, column.columnDef.header);
            },
        }),
        muiTopToolbarProps: {
            showColumnFilters: false,
            showGlobalFilter: true,
            enableHiding: false,
            sx: {
                position: "sticky",
                top: 0,
                zIndex: 1,
                "& .MuiBox-root": {
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                },
            },
        },
        renderTopToolbar: () => (
            <ListingHeader
                isHeader={isHeader}
                rightPane={true}
                parentType={parentType}
                type="ReshuffleRequest"
                btnLable="Add Request"
                searchedData={searchHandler}
                setIsCreate={() => setIsCreate(true)}
                projectId={projectId}
                isCreatable={false}
                isSearchDisable={cols.length}
                handleCreateFunc={() => { }}
                triggerSearch={triggerSearch}
            />
        ),
        renderBottomToolbarCustomActions: () => (
            <div className="bottom-toolbar-container">
                <div className="toolbar-content">
                    {loading || isFetchingMore ? (
                        <div className="loading-indicator">
                            <span>{`${!isFetchingMore ? 'Loading...' : 'Loading more data...'}`}</span>
                        </div>
                    ) : pagination?.TotalCount > 0 ? (
                        <div className="data-info">
                            <span className="data-text">
                                Showing <strong>{rows.length}</strong> of{" "}
                                <strong>{pagination?.TotalCount || 0}</strong> total records
                            </span>
                        </div>
                    ) : (
                        <div className="data-info">
                            <span className="data-text">No records Found</span>
                        </div>
                    )}
                </div>
            </div>
        ),
        muiBottomToolbarProps: {
            sx: {
                position: "absolute",
                bottom: 0,
                backgroundColor: "#f5f5f5",
                padding: "0.7rem 1rem",
                width: "100%",
                height: "38px",
            },
        },
        muiTableBodyProps: {
            sx: {
                fontFamily: "Figtree-Regular",
                fontSize: "13px",

                marginBottom: "66px",
                "& .MuiTableBody-Cell": {
                    "&:hover": {
                        backgroundColor: "#ece8fd15",
                    },
                },
            },
        },
        muiTableBodyRowProps: ({ row }) => ({
            sx: {
                height: "32px",
                position: "relative",
                "&:hover": {
                    backgroundColor: row.original.isSkeleton ? "transparent" : "#ece8fd15",
                },
                cursor: row.original.isSkeleton ? "default" : "pointer",
            },
            className: row.original.isSkeleton ? "" : "hoverable-row",
        }),
        muiTableContainerProps: ({ table }) => ({
            ref: tableContainerRef,
            sx: {
                height: `calc(100% - ${table.refs.topToolbarRef.current?.offsetHeight || 115
                    }px)`,
                overflow: "auto",
                "&::-webkit-scrollbar": {
                    height: "8px",
                    width: "4px",
                },
                "&::-webkit-scrollbar-track": {
                    backgroundColor: "#FFFFFF",
                    borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#342c82",
                    borderRadius: "4px",
                    "&:hover": {
                        backgroundColor: "#342c82",
                    },
                },
            },
        }),
        muiTablePaperProps: {
            sx: {
                height: "100%",
            },
        },
        muiSkeletonProps: {
            height: 34,
            sx: {
                background:
                    "linear-gradient(90deg, #e0e0e0 25%, #ECE7FD 50%, #e0e0e0 75%)",
                backgroundSize: "200% 100%",
                animation: "shimmer 1.5s infinite",
                "@keyframes shimmer": {
                    "0%": {
                        backgroundPosition: "200% 0",
                    },
                    "100%": {
                        backgroundPosition: "-200% 0",
                    },
                },
            },
        },
        muiTableBodyCellProps: ({ cell, column, table }) => ({
            sx: {
                fontFamily: "Figtree-Regular",
                fontSize: "13px",
                padding: "0.5rem 1rem",
                position: "relative",
                overflow: "visible",
                "&:focus": {
                    outline: "none",
                },
                "&:hover": {
                    fontFamily: "Figtree-Medium",
                    fontWeight: "bold",
                },
            },
        }),
        renderEmptyRowsFallback: () => {
            return loading ? (
                <div className="d-flex justify-content-center">
                    <Spin />
                </div>
            ) : rows.length === 0 ? (
                <div className="flex-grow flex items-center justify-center p-4">
                    <div className="h-full flex flex-col items-center justify-center min-h-[400px] p-8">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="65"
                            height="64"
                            viewBox="0 0 65 64"
                            fill="none"
                            className="mb-4"
                        >
                            <path
                                d="M6.9002 21.6008C6.9002 16.7407 10.8401 12.8008 15.7002 12.8008H49.3002C54.1603 12.8008 58.1002 16.7407 58.1002 21.6008V42.4008C58.1002 47.2609 54.1603 51.2008 49.3002 51.2008H32.8884L27.8968 46.2092C28.7964 44.3293 29.3002 42.2239 29.3002 40.0008C29.3002 32.0479 22.8531 25.6008 14.9002 25.6008C11.94 25.6008 9.18835 26.494 6.9002 28.0257V21.6008ZM14.9002 51.2008C17.416 51.2008 19.7381 50.3713 21.6079 48.9708L29.7688 57.132C30.3936 57.7569 31.4067 57.7569 32.0315 57.1321C32.6564 56.5072 32.6564 55.4942 32.0316 54.8693L23.8706 46.708C25.2708 44.8384 26.1002 42.5164 26.1002 40.0008C26.1002 33.8152 21.0858 28.8008 14.9002 28.8008C8.71461 28.8008 3.7002 33.8152 3.7002 40.0008C3.7002 46.1864 8.71461 51.2008 14.9002 51.2008ZM14.9002 48.0008C10.4819 48.0008 6.9002 44.4191 6.9002 40.0008C6.9002 35.5825 10.4819 32.0008 14.9002 32.0008C19.3185 32.0008 22.9002 35.5825 22.9002 40.0008C22.9002 44.4191 19.3185 48.0008 14.9002 48.0008Z"
                                fill="#342C82"
                            />
                        </svg>
                        <h3 className="text-xl font-semibold text-gray-800 mb-2">
                            No data currently available!
                        </h3>
                        <p className="text-gray-600 text-center max-w-md">
                            New entries will appear here once created.
                        </p>
                    </div>
                </div>
            ) : null;
        },
        muiLinearProgressProps: {
            sx: {
                height: "4px",
                backgroundColor: "#cfc9ff",
                "& .MuiLinearProgress-bar": {
                    backgroundColor: "#342c82",
                },
                "& .MuiLinearProgress-bar1Indeterminate": {
                    backgroundColor: "#342c82",
                },
                "& .MuiLinearProgress-bar2Indeterminate": {
                    backgroundColor: "#342c82",
                },
                "& .MuiLinearProgress-barColorPrimary": {
                    backgroundColor: "#342c82",
                },
                "& .MuiLinearProgress-colorPrimary": {
                    backgroundColor: "#cfc9ff",
                },
                "& .MuiLinearProgress-colorSecondary": {
                    backgroundColor: "#cfc9ff",
                },
                "& .MuiLinearProgress-barColorSecondary": {
                    backgroundColor: "#342c82",
                },
            },
        },
    });

    return <MaterialReactTable table={table} />;
};

export default ReshuffleRequestTable;
